/**
 * ScopeSync — keeps the scope and the URL agreeing, in both directions.
 *
 * Scope used to live only in React context plus localStorage, which meant a link to a scoped
 * page carried no scope: refresh it and you got whatever site that browser last happened to
 * pick, and a link pasted to a colleague opened on THEIR site. For an operator sending
 * "look at this" during an incident, that is the difference between a useful link and a
 * misleading one.
 *
 * So `?site=<nodeId>` (or `?site=all`) rides on every scoped URL:
 *
 *   URL → scope   on arrival, an explicit `?site=` wins over the stored selection. This is what
 *                 makes a shared link land where its sender was.
 *   scope → URL   afterwards, the param is kept in step with the switcher, replacing rather
 *                 than pushing so the back button steps through pages, not through scope edits.
 *
 * The param is only written on scoped pages. Platform-admin pages read the cloud's own database
 * and mean the same thing under any scope, so tagging them would be noise.
 *
 * Renders nothing — it's a synchroniser, mounted once by the Layout.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useNode } from '../../contexts/NodeContext';
import { ALL_SITES, SCOPE_PARAM, isAllSitesPath, isUnscoped, resolveScopeSwitch } from '../../constants/scope';

export default function ScopeSync() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { nodes, selectedNodeId, selectNode, loading } = useNode();

  const urlScope = params.get(SCOPE_PARAM);
  const currentScope = selectedNodeId ?? ALL_SITES;

  // Adopt an incoming `?site=` ONCE per value. Without this latch, the scope→URL effect below
  // and this one would take turns overwriting each other every render.
  const adoptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!urlScope || adoptedRef.current === urlScope) return;

    if (urlScope === ALL_SITES) {
      adoptedRef.current = urlScope;
      if (selectedNodeId) selectNode(null);
      return;
    }

    // Wait for the node list before judging an unknown id — on a cold load the list is still in
    // flight, and rewriting the URL now would silently discard a perfectly good shared link.
    if (loading) return;
    adoptedRef.current = urlScope;
    if (nodes.some((n) => n.nodeId === urlScope)) {
      if (selectedNodeId !== urlScope) selectNode(urlScope);
    }
    // An id that isn't ours (revoked access, decommissioned site) falls through: the scope→URL
    // effect below rewrites the param to whatever scope is actually in force.
  }, [urlScope, loading, nodes, selectedNodeId, selectNode]);

  /**
   * A per-site page opened while the scope is All Sites has no site to fetch from — it would
   * render a "select a site" empty state pointing at a control the operator has already used.
   * Send them to the all-sites counterpart instead, by the same rules the switcher uses.
   *
   * Held until the node list has settled, so a stored site still resolving doesn't get read as
   * All Sites and bounce a perfectly valid deep link.
   */
  useEffect(() => {
    if (loading || selectedNodeId) return;
    if (isUnscoped(pathname) || isAllSitesPath(pathname)) return;
    const target = resolveScopeSwitch(pathname, true);
    if (target) navigate(target, { replace: true });
  }, [loading, selectedNodeId, pathname, navigate]);

  useEffect(() => {
    if (isUnscoped(pathname)) {
      if (!urlScope) return;
      const next = new URLSearchParams(params);
      next.delete(SCOPE_PARAM);
      setParams(next, { replace: true });
      return;
    }

    // Hold off while a pending `?site=` is still being adopted, or the stale scope would be
    // written back over the link the operator just opened.
    if (urlScope && adoptedRef.current !== urlScope) return;
    if (urlScope === currentScope) return;

    const next = new URLSearchParams(params);
    next.set(SCOPE_PARAM, currentScope);
    adoptedRef.current = currentScope;
    setParams(next, { replace: true });
  }, [pathname, currentScope, urlScope, params, setParams]);

  return null;
}
